import {
  Button,
  EmptyState,
  FilterSegments,
  RowActions,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  formatRelative,
  toast,
} from '@westhetics/ui'
import { Link, useSearchParams } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import {
  useDeliveries,
  useRetryDelivery,
  useWebhooks,
  type Delivery,
  type DeliveryFilter,
  type Integration,
} from './api'
import { deliveryStatus, nextAttemptText, responseText, urlHost } from './labels'

/**
 * Aba Entregas (RN-INT-17 e 18): a lista de trabalho das entregas de webhook do app, das mais novas para as mais
 * antigas. Abre em "Com falha" quando há falha, porque é o que pede ação; "Reenviar" põe a entrega na fila de novo.
 * O filtro fica no endereço e sobrevive à navegação.
 */

const FILTERS: DeliveryFilter[] = ['failed', 'pending', 'all']
const FILTER_LABELS: Record<DeliveryFilter, string> = {
  failed: 'Com falha',
  pending: 'Na fila',
  all: 'Todas',
}

export function DeliveriesTab({ integration }: { integration: Integration }) {
  const [params, setParams] = useSearchParams()
  const fromUrl = params.get('filter') as DeliveryFilter | null
  const filter: DeliveryFilter =
    fromUrl && FILTERS.includes(fromUrl)
      ? fromUrl
      : integration.deliveries.failed > 0
        ? 'failed'
        : 'all'
  const page = Math.max(1, Number(params.get('page')) || 1)
  const query = useDeliveries(integration.id, { filter, page })
  const webhooks = useWebhooks(integration.id)
  const retry = useRetryDelivery(integration.id)
  const items = query.data?.data ?? []
  const counts = query.data?.meta.counts

  function go(next: { filter?: DeliveryFilter; page?: number }) {
    const nextParams = new URLSearchParams(params)
    if (next.filter) {
      nextParams.set('filter', next.filter)
      nextParams.delete('page')
    }
    if (next.page) nextParams.set('page', String(next.page))
    setParams(nextParams, { replace: true })
  }

  function hostOf(delivery: Delivery) {
    const webhook = webhooks.data?.find((w) => w.id === delivery.webhookId)
    return webhook ? urlHost(webhook.url) : 'webhook removido'
  }

  function resend(delivery: Delivery) {
    retry.mutate(delivery.id, {
      onSuccess: () => toast('Entrega na fila; sai no próximo minuto'),
      onError: (error) => toast(`Não foi possível reenviar: ${errorMessage(error)}`),
    })
  }

  return (
    <div className="flex flex-col gap-4">
      <FilterSegments
        label="Filtrar entregas"
        value={filter}
        onChange={(value) => go({ filter: value })}
        options={FILTERS.map((value) => ({
          value,
          label: FILTER_LABELS[value],
          count: counts?.[value],
        }))}
      />

      {query.isError ? (
        <LoadError
          title="Não conseguimos carregar as entregas"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : query.isPending ? (
        <TableSkeleton rows={4} columns={6} />
      ) : items.length === 0 ? (
        filter === 'failed' ? (
          <EmptyState
            title="Nenhuma entrega com falha"
            description="Tudo o que saiu chegou ao sistema da clínica com resposta 2xx."
            action={
              <Button variant="outline" onClick={() => go({ filter: 'all' })}>
                Ver todas
              </Button>
            }
          />
        ) : filter === 'pending' ? (
          <EmptyState
            title="Nada na fila"
            description="As entregas saem a cada minuto; quando falham, voltam para cá até a próxima tentativa."
            action={
              <Button variant="outline" onClick={() => go({ filter: 'all' })}>
                Ver todas
              </Button>
            }
          />
        ) : (
          <EmptyState
            title="Nenhuma entrega ainda"
            description="As entregas aparecem quando algo acontece na clínica e o app tem webhook ativo. As dos últimos 30 dias ficam aqui."
            action={
              <Button variant="outline" asChild>
                <Link to={`${routes.admin.integration(integration.id)}?tab=webhooks`}>
                  Ver os webhooks
                </Link>
              </Button>
            }
          />
        )
      ) : (
        <Table>
          <TableHeader>
            <tr>
              <TableHead>Evento</TableHead>
              <TableHead>Webhook</TableHead>
              <TableHead>Resposta</TableHead>
              <TableHead>Tentativas</TableHead>
              <TableHead>Quando</TableHead>
              <TableHead>Status</TableHead>
              <TableHead aria-label="Ações" />
            </tr>
          </TableHeader>
          <TableBody>
            {items.map((delivery) => {
              const status = deliveryStatus(delivery)
              return (
                <TableRow key={delivery.id}>
                  <TableCell primary>
                    <code className="font-body text-[13px] font-normal">{delivery.eventType}</code>
                  </TableCell>
                  <TableCell label="Webhook" muted className="break-all">
                    {hostOf(delivery)}
                  </TableCell>
                  <TableCell label="Resposta" muted>
                    {responseText(delivery)}
                  </TableCell>
                  <TableCell label="Tentativas" muted numeric>
                    {delivery.attempts} de 8
                  </TableCell>
                  <TableCell label="Quando" muted>
                    {formatRelative(new Date(delivery.createdAt))}
                    {delivery.status === 'pending' && delivery.nextAttemptAt
                      ? ` · tenta de novo ${nextAttemptText(delivery.nextAttemptAt)}`
                      : ''}
                  </TableCell>
                  <TableCell label="Status">
                    <StatusTag level={status.level}>{status.label}</StatusTag>
                  </TableCell>
                  <TableCell>
                    {delivery.status === 'pending' || integration.status !== 'active' ? null : (
                      <RowActions>
                        <button type="button" onClick={() => resend(delivery)}>
                          reenviar
                        </button>
                      </RowActions>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
      {query.data && items.length > 0 ? (
        <ListFooter
          meta={query.data.meta}
          noun={['entrega', 'entregas']}
          note="das mais novas para as mais antigas · 30 dias"
          onPage={(next) => go({ page: next })}
        />
      ) : null}
    </div>
  )
}
